/**
 * Contains utilities for verifying integrity of Doc and Editor
 * @module
 */
import { isWallPlaneHorizontalDatum } from "building-types/datums/datumEntityTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { type DocRef } from "building-types/docRef";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import {
  isWallFaceRule,
  isWallPlaneAnchoredRule,
  wallFaceRuleHasConstraint,
} from "building-types/walls2/wallPlaneRules";
import Flags from "common/constants/flags";
import { groupBy } from "common/utils/arrays";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import objectGroupBy from "common/utils/objectGroupBy";
import stableHash from "common/utils/stableHash";
import { containsUid } from "common/utils/uid";
import type { DocTableFieldNames } from "./docTableFieldNames";
import { copyForElevationOptionSet, getElevationOptionSet } from "./elevation";
import { docEntityHandleHash } from "./entity/core/handleHash";
import { docGetEntityHandleByHash } from "./entity/docEntityHandleHash";
import { docEntityTableFieldsToEntityType } from "./entity/docEntityTableFieldsToEntityType";
import { docEntityTypeToDocTableName } from "./entity/docEntityTypeToDocTableName";
import { docGetEntityHandleById } from "./entity/docGetEntityHandleById";
import type { DocIntegrityIssue, InvalidDocEntity } from "./integrityTypes";
import { IntegrityIssueType } from "./integrityTypes";
import { docToOptionDefinitions } from "./options/docGetInstanceOptionSets";
import { getOptionSetArchetypeOrSelf } from "./options/getOptionSetArchetypeOrSelf";

const isJest = process.env.NODE_ENV === "test" || process.env.JEST_WORKER_ID !== undefined;

export function logIntegrityIssues(issues: DocIntegrityIssue[]): void {
  if (issues.length === 0) {
    return;
  }

  if (isJest) {
    const groupedIssues = groupBy(issues, (issue) => issue.type);
    console.warn(`Jest environment detected, itemized integrity issue logging for ${issues.length} issues`);
    console.error(groupedIssues);
    return;
  }

  // Convert array to object with indices as keys for objectGroupBy
  const issuesObj = issues.reduce(
    (acc, issue, index) => {
      acc[index] = issue;
      return acc;
    },
    {} as Record<number, DocIntegrityIssue>,
  );

  // Group issues by type
  const groupedIssues = objectGroupBy(issuesObj, "type");

  // Then show detailed collapsible logs grouped by type
  console.groupCollapsed(`%c${issues.length} Critical Integrity Issue(s):`, "color: #ff5252; font-weight: bold;");

  for (const type in groupedIssues) {
    const typeIssues = groupedIssues[type] as DocIntegrityIssue[];
    console.groupCollapsed(`${type} (${typeIssues.length} issues)`);

    typeIssues.forEach((issue, index) => {
      console.groupCollapsed(`Issue ${String(index + 1).padStart(2, "0")}`);

      // Log the full message without truncation, highlighting variables in red with monospace font
      const parts = issue.message.split(/([A-Z]+\("[^"]+"\))/);
      const styles = parts.map((part) =>
        part.match(/[A-Z]+\("[^"]+"\)/) ? "color: #ff5252; font-family: monospace;" : "",
      );

      console.log("%cMessage:%c " + parts.join("%c"), "font-weight: bold;", ...styles);

      // Log additional context based on issue type
      switch (issue.type) {
        case IntegrityIssueType.INVALID_SELF_REFERENCE:
        case IntegrityIssueType.INVALID_REFERENCE: {
          console.log("%cSource:", "font-weight: bold;", issue.srcHash);
          console.log("%cTarget:", "font-weight: bold;", issue.dstHash);
          if (issue.docRef) {
            console.log("%cDoc Ref:", "font-weight: bold;", issue.docRef.id);
          }
          break;
        }

        case IntegrityIssueType.KEY_ID_MISMATCH: {
          console.log("%cTable:", "font-weight: bold;", issue.tableName);
          console.log("%cEntity Hash:", "font-weight: bold;", issue.srcHash);
          console.log("%cExpected ID:", "font-weight: bold;", issue.id);
          if (issue.docRef) {
            console.log("%cDoc Ref:", "font-weight: bold;", issue.docRef.id);
          }
          break;
        }

        case IntegrityIssueType.INVALID_DOC_ENTITY: {
          console.log("%cEntity Type:", "font-weight: bold;", issue.srcEntityType);
          console.log("%cEntity Hash:", "font-weight: bold;", issue.srcHash);
          if (issue.docRef) {
            console.log("%cDoc Ref:", "font-weight: bold;", issue.docRef.id);
          }
          break;
        }

        case IntegrityIssueType.DOC_EMPTY:
        case IntegrityIssueType.ELEVATION_MIRROR_DESYNC:
        case IntegrityIssueType.INVALID_DOC_VERSION: {
          // These types only have a message, no additional context needed
          break;
        }
      }

      console.groupEnd();
    });

    console.groupEnd();
  }

  console.groupEnd();
}

function getEntityTypeFromTableName(tableName: string): DocEntityType | undefined {
  // Find the entity type that maps to this table name
  const entityType = Object.entries(docEntityTypeToDocTableName).find(([_, table]) => table === tableName)?.[0] as
    | DocEntityType
    | undefined;

  return entityType;
}

export function docAssertOrEmitKeyIdMismatchIssue<T extends DocEntityType>(
  doc: Doc,
  entity: DocEntityBase<T>,
  key: string,
  tableName: keyof Doc,
  issues: DocIntegrityIssue[],
  docRef?: DocRef,
): void {
  if (!entity) {
    const derivedEntityType = getEntityTypeFromTableName(String(tableName));
    const entityHash = docEntityHandleHash({ type: derivedEntityType as DocEntityType, id: key } as DocEntityHandle);
    issues.push({
      type: IntegrityIssueType.KEY_ID_MISMATCH,
      srcEntityType: derivedEntityType ?? ("UNKNOWN" as DocEntityType),
      key,
      id: "undefined",
      srcHash: entityHash,
      message: `Found null/undefined entity in table "${String(
        tableName,
      )}" at key "${key}" (expected type: ${derivedEntityType})`,
      tableName: String(tableName),
      docRef,
    });
    return;
  }

  const mismatchedId = entity.id !== key;
  let handle: DocEntityHandle | null = null;

  if (key && containsUid(key)) {
    handle = docGetEntityHandleById(doc, key, [entity.entityType]);
  } else if (entity.id) {
    handle = docGetEntityHandleById(doc, entity.id, [entity.entityType]);
  } else {
    console.warn({
      message: `${entity.entityType} in table "${String(tableName)}" has an invalid key or id`,
      key,
      entityId: entity.id,
      entityType: entity.entityType,
    });
  }

  if (mismatchedId) {
    const entityHash = handle ? docEntityHandleHash(handle) : "N/A";

    issues.push({
      type: IntegrityIssueType.KEY_ID_MISMATCH,
      srcEntityType: entity.entityType,
      key,
      id: String(entity.id),
      srcHash: entityHash,
      message: `Key-ID mismatch in table "${String(tableName)}": key "${key}" does not match entity id "${
        entity.id
      }" (entityType: ${entity.entityType})`,
      tableName: String(tableName),
      docRef,
    });
  }
}

export function docAssertOrEmitInvalidDocEntityIssue<T extends DocEntityType>(
  _doc: Doc,
  entity: Partial<DocEntityBase<T>>,
  expectedEntityType: T,
  issues: DocIntegrityIssue[],
  docRef?: DocRef,
): void {
  if (entity.entityType !== expectedEntityType) {
    const entityHash = entity?.id
      ? docEntityHandleHash({ type: expectedEntityType, id: String(entity.id) } as DocEntityHandle)
      : "N/A";
    const issue: InvalidDocEntity = {
      type: IntegrityIssueType.INVALID_DOC_ENTITY,
      srcEntityType: expectedEntityType,
      srcHash: entityHash,
      message: `Wrong entityType -- expected ${expectedEntityType} but got ${entity.entityType}`,
      docRef,
    };
    issues.push(issue);
  }
}

/**
 * Comprehensive function to check document integrity using docEntityTableFieldsToEntityType mapping
 * and DocEntityNodeRegistry to automate checks across all tables.
 *
 * This approach ensures:
 * 1. We don't need to itemize checks for each entity type
 * 2. New entity types will automatically be included in integrity checks
 * 3. Dependencies between entities are properly checked
 *
 * @param doc The document to check for integrity issues
 * @returns An array of integrity issues found
 */
export function docCheckIntegrity(doc: Doc): DocIntegrityIssue[] {
  const issues: DocIntegrityIssue[] = [];

  // Disabled outside of development mode to avoid performance overhead
  if (!(Flags.IS_DEV_MODE || isJest)) {
    return issues;
  }

  const directIntegrityTables: DocTableFieldNames[] = [];
  const referenceIntegrityTables: DocTableFieldNames[] = [];

  for (const field in docEntityTableFieldsToEntityType) {
    const tableName = field as DocTableFieldNames;

    const tableEntityType = docEntityTableFieldsToEntityType[tableName];

    if (tableEntityType) {
      directIntegrityTables.push(tableName);
      continue;
    }

    switch (tableName) {
      case "entityRoomLayouts":
      case "entityGroups":
      case "entityRefs":
      case "optionChoices":
        referenceIntegrityTables.push(tableName);
        break;

      default: {
        break;
      }
    }
  }

  for (const tableName of directIntegrityTables) {
    const srcEntityType = docEntityTableFieldsToEntityType[tableName];
    assertIsNotUndefined(srcEntityType);

    const table = doc[tableName];
    assertIsNotUndefined(table);

    for (const entityId in table) {
      const entity = table[entityId];

      const docRefId = doc.entityRefs[entityId]?.docRefId;
      const docRef = doc.docRefs[docRefId];

      // Check key-id match
      docAssertOrEmitKeyIdMismatchIssue(
        doc,
        entity as DocEntityBase<DocEntityType>,
        entityId,
        tableName as keyof Doc,
        issues,
        docRef,
      );

      // Check entity type correctness
      docAssertOrEmitInvalidDocEntityIssue(
        doc,
        entity as Partial<DocEntityBase<DocEntityType>>,
        srcEntityType,
        issues,
        docRef,
      );
    }
  }

  for (const rule of Object.values(doc.wallPlaneRules ?? {})) {
    const ruleHash = docEntityHandleHash({ type: "WALL_PLANE_RULE", id: rule.id });
    if (isWallFaceRule(rule)) {
      const filterId = rule.filterId;
      const filter = filterId ? doc.wallPlaneFilters[filterId] : undefined;
      if (filterId && !filter) {
        issues.push({
          type: IntegrityIssueType.INVALID_REFERENCE,
          srcEntityType: "WALL_PLANE_RULE",
          dstEntityType: "WALL_PLANE_FILTER",
          srcHash: ruleHash,
          dstHash: docEntityHandleHash({ type: "WALL_PLANE_FILTER", id: filterId }),
          message: `Rule ${rule.id} references missing filter ${filterId}`,
        });
      }
      if (!wallFaceRuleHasConstraint(rule)) {
        issues.push({
          type: IntegrityIssueType.INVALID_DOC_ENTITY,
          srcEntityType: "WALL_PLANE_RULE",
          srcHash: ruleHash,
          message: `Wall face rule ${rule.id} must reference a catalog filter, include instance constraints, or set a PERIMETER/OPPOSITE subFilter`,
        });
      }
    } else if (isWallPlaneAnchoredRule(rule)) {
      if (!rule.planeHash) {
        issues.push({
          type: IntegrityIssueType.INVALID_DOC_ENTITY,
          srcEntityType: "WALL_PLANE_RULE",
          srcHash: ruleHash,
          message: `Wall plane rule ${rule.id} must specify a plane hash`,
        });
      }
    }

    const operationAABB = rule.operationBoundary?.boundaryType === "AABB" ? rule.operationBoundary.aabb : undefined;
    if (operationAABB) {
      for (const side of [operationAABB.start, operationAABB.end]) {
        if (side?.mode !== "HORIZONTAL_DATUM") {
          continue;
        }
        const horizontalDatumId = side.horizontalDatumId;
        const horizontalDatum = doc.datums?.[horizontalDatumId];
        if (!horizontalDatum || !isWallPlaneHorizontalDatum(horizontalDatum)) {
          issues.push({
            type: IntegrityIssueType.INVALID_REFERENCE,
            srcEntityType: "WALL_PLANE_RULE",
            dstEntityType: "DATUM",
            srcHash: ruleHash,
            dstHash: docEntityHandleHash({ type: "DATUM", id: horizontalDatumId }),
            message: `Rule ${rule.id} references missing wall-plane horizontal datum ${horizontalDatumId}`,
          });
        }
      }
    }
  }

  for (const datum of Object.values(doc.datums ?? {})) {
    if (datum.datumType !== "WALL_PLANE" || datum.direction !== "HORIZONTAL") {
      continue;
    }
    const datumHash = docEntityHandleHash({ type: "DATUM", id: datum.id });
    if (!datum.planeHash) {
      issues.push({
        type: IntegrityIssueType.INVALID_DOC_ENTITY,
        srcEntityType: "DATUM",
        srcHash: datumHash,
        message: `Wall plane horizontal datum ${datum.id} must specify a plane hash`,
      });
    }
  }

  for (const tableName of referenceIntegrityTables) {
    switch (tableName) {
      case "entityRoomLayouts": {
        for (const hash in doc.entityRoomLayouts) {
          const roomLayoutId = doc.entityRoomLayouts[hash];
          const roomLayout = doc.roomLayouts[roomLayoutId];

          if (!roomLayout) {
            const roomLayoutDocRefId = doc.entityRefs[roomLayoutId]?.docRefId;
            const roomLayoutDocRef = doc.docRefs[roomLayoutDocRefId];

            issues.push({
              type: IntegrityIssueType.INVALID_REFERENCE,
              srcEntityType: "ROOM_LAYOUT",
              dstEntityType: "ROOM_LAYOUT",
              srcHash: hash,
              dstHash: roomLayoutId,
              message: `Room layout not found for entityRoomLayouts[${hash}]`,
              docRef: roomLayoutDocRef,
            });
          }

          const parentRoom = roomLayout ? doc.rooms[roomLayout.parentId] : undefined;

          if (!parentRoom) {
            const roomDocRefId = roomLayout ? doc.entityRefs[roomLayout.parentId]?.docRefId : undefined;
            const roomDocRef = roomDocRefId ? doc.docRefs[roomDocRefId] : undefined;
            const roomHash = roomLayout ? docEntityHandleHash({ type: "ROOM", id: roomLayout.parentId }) : "N/A";

            issues.push({
              type: IntegrityIssueType.INVALID_REFERENCE,
              srcEntityType: "ROOM_LAYOUT",
              dstEntityType: "ROOM",
              srcHash: hash,
              dstHash: roomHash,
              message: `Room not found for entityRoomLayouts[${hash}].parentId: ${roomLayout?.parentId}`,
              docRef: roomDocRef,
            });
          }
        }
        break;
      }
      case "entityGroups": {
        for (const hash in doc.entityGroups) {
          const groupId = doc.entityGroups[hash];
          const group = doc.groups[groupId];
          const docRefId = doc.entityRefs[groupId]?.docRefId;
          const docRef = doc.docRefs[docRefId];

          const entityHandle = docGetEntityHandleByHash(doc, hash);

          if (!entityHandle) {
            issues.push({
              type: IntegrityIssueType.INVALID_REFERENCE,
              srcHash: hash,
              dstEntityType: "GROUP",
              dstHash: group ? docEntityHandleHash({ type: "GROUP", id: group.id }) : "N/A",
              message: `Entity not found for group`,
              docRef,
            });
          }

          if (!group) {
            issues.push({
              type: IntegrityIssueType.INVALID_REFERENCE,
              srcEntityType: "GROUP",
              dstEntityType: "GROUP",
              srcHash: hash,
              dstHash: groupId ? docEntityHandleHash({ type: "GROUP", id: groupId }) : "N/A",
              message: `Group not found for entityGroups[${hash}]`,
              docRef,
            });
          }

          break;
        }
      }
      case "entityRefs": {
        for (const id in doc.entityRefs) {
          const entityRef = doc.entityRefs[id];
          const docRefId = entityRef?.docRefId;
          const docRef = docRefId ? doc.docRefs[docRefId] : undefined;

          const entityHandle = docGetEntityHandleById(doc, entityRef.id, [entityRef.entityType]);

          if (!entityHandle) {
            issues.push({
              type: IntegrityIssueType.INVALID_REFERENCE,
              srcEntityType: "ENTITY_REF" as DocEntityType,
              dstHash: entityRef.id,
              dstEntityType: "UNKNOWN" as DocEntityType,
              message: `Entity not found for entityRefs[${id}].entityId: ${entityRef.id}`,
              docRef,
            });
          }
        }
        break;
      }
      case "optionChoices": {
        for (const optionSetId in doc.optionChoices) {
          const optionKey = doc.optionChoices[optionSetId];
          const optionSet = optionSetId
            ? getOptionSetArchetypeOrSelf(docToOptionDefinitions(doc), optionSetId)
            : undefined;
          const docRefId = optionSetId ? doc.entityRefs[optionSetId]?.docRefId : undefined;
          const docRef = docRefId ? doc.docRefs[docRefId] : undefined;

          if (!optionSet) {
            issues.push({
              type: IntegrityIssueType.INVALID_REFERENCE,
              srcEntityType: "OPTION_CHOICE" as DocEntityType,
              dstEntityType: "OPTION",
              dstHash: optionKey ? docEntityHandleHash({ type: "OPTION", optionSetId, optionKey }) : "N/A",
              message: `Option set not found for optionChoices[${optionSetId}]`,
              docRef,
            });
          }
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  docAssertOrEmitElevationMirrorDesyncIssue(doc, issues);

  return issues;
}

/**
 * `doc.elevations` and `doc.optionSets.elevationStyle` are two spellings of the same data, and every
 * elevation write has to move both. In sync, rebuilding the option set from the table reproduces it
 * exactly -- `copyForElevationOptionSet` prefers the existing option's `name`/`price` and derives the
 * parallel maps from the rows -- so a hash mismatch means some write moved one table and not the other.
 *
 * This catches a missed write path without enumerating them. It has to exist because
 * `docMarshal` re-runs the same rebuild on every load, which silently heals a desync before any test
 * can see it.
 */
function docAssertOrEmitElevationMirrorDesyncIssue(doc: Doc, issues: DocIntegrityIssue[]): void {
  const elevationOptionSet = getElevationOptionSet(doc.optionSets);

  if (!elevationOptionSet) {
    return;
  }

  const rebuilt = copyForElevationOptionSet(elevationOptionSet, doc.elevations ?? {});

  if (stableHash(rebuilt) === stableHash(elevationOptionSet)) {
    return;
  }

  issues.push({
    type: IntegrityIssueType.ELEVATION_MIRROR_DESYNC,
    srcEntityType: "OPTION_SET",
    srcHash: docEntityHandleHash({ type: "OPTION_SET", id: elevationOptionSet.id }),
    message: `Elevation option set OPTION_SET("${elevationOptionSet.id}") does not match doc.elevations -- an elevation write moved one table and not the other`,
  });
}
